/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { Portal } from '@radix-ui/react-portal'
import { motion } from 'framer-motion'
import { ChevronDown, ChevronUp } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import * as React from 'react'

import { ExternalLink } from '@/components/fragments/external-link'
import { MENU_LINKS } from '@/constants/menu-links'
import { Button, buttonVariants } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Routes } from '@/constants/routes'
import { cn } from '@/lib/utils'
import { useLingui } from '@lingui/react'
import Image from 'next/image'
import Logo from 'public/landing/logos/group.png'
import { Switcher } from './switcher-language'

export function MobileMenu({
  className,
  ...other
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  const [open, setOpen] = React.useState<boolean>(false)
  const pathname = usePathname()

  React.useEffect(() => {
    const handleRouteChangeStart = () => {
      if (document.activeElement instanceof HTMLInputElement) {
        document.activeElement.blur()
      }

      setOpen(false)
    }

    handleRouteChangeStart()
  }, [pathname])

  const handleChange = () => {
    const mediaQueryList = window.matchMedia('(min-width: 1024px)')
    setOpen((open) => (open ? !mediaQueryList.matches : false))
  }

  React.useEffect(() => {
    handleChange()
    const mediaQueryList = window.matchMedia('(min-width: 1024px)')
    mediaQueryList.addEventListener('change', handleChange)
    return () => mediaQueryList.removeEventListener('change', handleChange)
  }, [])

  const handleToggleMobileMenu = (): void => {
    setOpen((open) => !open)
  }

  return (
    <>
      <div className={cn('flex items-center justify-between', className)} {...other}>
        <Link href={Routes.Root}>
          <Image
            property='true'
            src={Logo}
            width={377}
            height={84}
            className='w-auto max-h-10'
            alt='Logo Pione'
          />
        </Link>
        <Button
          variant='ghost'
          size='icon'
          aria-expanded={open}
          aria-label='Toggle Mobile Menu'
          onClick={handleToggleMobileMenu}
          className='flex flex-col items-center justify-center rounded-full select-none aspect-square h-fit'
        >
          <motion.div
            className='w-5 origin-center border-t-2 !border-muted-foreground'
            initial={{ translateY: '-3px' } as any}
            animate={
              open ?
                ({ rotate: '45deg', translateY: '1px' } as any)
              : { translateY: '-3px', rotate: '0deg' }
            }
            transition={{ bounce: 0, duration: 0.1 }}
          />
          <motion.div
            className='w-5 origin-center border-t-2 !border-muted-foreground'
            transition={{ bounce: 0, duration: 0.1 }}
            initial={{ translateY: '3px' } as any}
            animate={
              open ?
                ({ rotate: '-45deg', translateY: '-1px' } as any)
              : { translateY: '3px', rotate: '0deg', scaleX: 1 }
            }
          />
        </Button>
      </div>
      {open && (
        <Portal asChild>
          {/* <RemoveScroll allowPinchZoom enabled> */}
          <MainMobileMenu onLinkClicked={handleToggleMobileMenu} />
          {/* </RemoveScroll> */}
        </Portal>
      )}
    </>
  )
}

type MainMobileMenuProps = {
  onLinkClicked: () => void
}

function MainMobileMenu({ onLinkClicked }: MainMobileMenuProps): React.JSX.Element {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({})
  const { i18n } = useLingui()

  return (
    <div className='fixed inset-0 z-50 mt-[65px] overflow-y-auto bg-background animate-in fade-in-0'>
      <div className='flex flex-col items-start p-4 space-y-3 size-full'>
        <ul className='w-full'>
          {MENU_LINKS.map((item) => (
            <li key={i18n._(item.title)} className='py-2'>
              {item.items ?
                <Collapsible
                  open={expanded[i18n._(item.title).toLowerCase()]}
                  onOpenChange={(isOpen) =>
                    setExpanded((prev) => ({
                      ...prev,
                      [i18n._(item.title).toLowerCase()]: isOpen
                    }))
                  }
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      type='button'
                      variant='ghost'
                      className='flex items-center justify-between w-full px-4 text-left h-9'
                    >
                      <span className='text-base font-medium'>{i18n._(item.title)}</span>
                      {expanded[i18n._(item.title).toLowerCase()] ?
                        <ChevronUp className='size-4' />
                      : <ChevronDown className='size-4' />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className='pl-4 mt-2'>
                      {item.items.map((subItem) => (
                        <li key={i18n._(subItem.title)}>
                          <Link
                            href={subItem.href || '#'}
                            target={subItem.external ? '_blank' : undefined}
                            rel={subItem.external ? 'noopener noreferrer' : undefined}
                            className={cn(
                              buttonVariants({ variant: 'ghost' }),
                              'm-0 h-auto w-full justify-start gap-5 p-2 px-3'
                            )}
                            onClick={onLinkClicked}
                          >
                            <span className='font-medium'>
                              {i18n._(subItem.title)}
                              {subItem.external && (
                                <ExternalLink className='inline ml-1 -mt-3 text-muted-foreground' />
                              )}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              : <Link
                  href={item.href || '#'}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                  className={cn(buttonVariants({ variant: 'ghost' }), 'w-full justify-start')}
                  onClick={onLinkClicked}
                >
                  <span className='text-base'>{i18n._(item.title)}</span>
                </Link>
              }
            </li>
          ))}
        </ul>

        <div className='flex items-center justify-between w-full gap-2 p-4 py-2 border-y border-border/40'>
          <div className='text-base font-medium'>Language</div>
          <Switcher />
        </div>
      </div>
    </div>
  )
}
